/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import { getCartState, ICartState } from '@msdyn365-commerce/global-state';
import { FeatureState } from '@msdyn365-commerce/retail-proxy';
import { Cart, CartLine, ProductSearchCriteria } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';

import {
    ArrayExtensions,
    createGetFeatureStateInput,
    getCustomer,
    GetCustomerInput,
    getFeatureState
} from '@msdyn365-commerce-modules/retail-actions';
import getSimpleProducts, { ProductInput } from './custom-get-simple-products';
import { getCategoryPathsAsync, searchByCriteriaAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { applyAttributesToProduct } from '../shared-utils/attributes';
import { SimpleProductExt } from '../shared-utils/types';
import { getProductListsByProductIdsAsync, IProductListByProductId } from './get-product-lists-by-product-ids';

/**
 * Input class for activeCartWithProducts data action.
 */
export class ActiveCartProductsInput implements IActionInput {
    public getCacheKey = () => 'ActiveCartProductsCustom';

    public getCacheObjectType = () => 'ActiveCartProducts';

    public dataCacheType = (): CacheType => 'none';
}

const createInput = (inputData: ICreateActionContext) => {
    return new ActiveCartProductsInput();
};

/**
 * Calls the Retail API and returns a cart object based on the passed GetCartInput.
 * @param input
 * @param ctx
 */
export async function getActiveCartProductsAction(input: ActiveCartProductsInput, ctx: IActionContext): Promise<SimpleProductExt[]> {
    // If no cart ID is provided in input, we need to create a cart object
    if (!input) {
        throw new Error('[getActiveCartWithProducts]No valid Input was provided, failing');
    }

    const cartState = await getCartState(ctx);
    const cart = cartState.cart;

    const productIds = cart.CartLines?.map(cartLine => cartLine.ProductId!).filter(Boolean) || [];
    const isQuantityLimitsFeatureIsOn: boolean = await isOrderQuantityLimitsFeatureEnabled(ctx);
    const productSearchCriteria: ProductSearchCriteria = {
        Context: {
            CatalogId: ctx.requestContext.apiSettings.catalogId,
            ChannelId: ctx.requestContext.apiSettings.channelId
        },
        Ids: productIds,
        IncludeAttributes: true
    };

    const getProductListsByProductIdsPromise = new Promise<IProductListByProductId[]>((resolve) => {
        return getProductListsByProductIdsAsync({ callerContext: ctx }, productIds)
            .then(resolve)
            .catch(() => resolve([]));
    });

    const getCategoryPathsPromise = getCategoryPathsAsync({ callerContext: ctx },
        ctx.requestContext.apiSettings.channelId,
        ctx.requestContext.apiSettings.catalogId,
        productIds.map(p => ({ ProductId: p }))
    );

    const [products, searchResults, productListsByProductId, categoryPaths] = await Promise.all([
        isQuantityLimitsFeatureIsOn ? getActiveCartProductsActionWhenQuantityLimitsFeatureIsOn(cart, ctx) : getActiveCartProducts(cartState, cart, ctx),
        productSearchCriteria.Ids!.length > 0 ? searchByCriteriaAsync({ callerContext: ctx }, productSearchCriteria) : Promise.resolve([]),
        getProductListsByProductIdsPromise,
        getCategoryPathsPromise
    ]);
    const itemTaxGroupMapping: Record<string, string> = {
        'F': '0%',
        'H': '25%',
        'M': '15%',
        'L': '12%'
    };

    for (const product of products) {
        const itemTaxGroupId = cart.CartLines?.find(x => x.ProductId === product.RecordId)?.ItemTaxGroupId;

        applyAttributesToProduct(ctx.requestContext,
            product,
            searchResults.find(sr => sr.RecordId === product.RecordId)?.AttributeValues,
            categoryPaths.find(cp => cp.ProductId === product.RecordId)?.CategoryPath,
            true
        );
        product.PartOfProductLists = productListsByProductId.filter(x => x.ProductId === product.RecordId);
        product.VAT = itemTaxGroupId ? itemTaxGroupMapping[itemTaxGroupId] : undefined;
    }

    return products;
}

export const getActiveCartProductsActionDataAction = createObservableDataAction({
    id: 'custom-get-products-in-active-cart',
    action: <IAction<SimpleProductExt[]>>getActiveCartProductsAction,
    input: createInput
});
export default getActiveCartProductsActionDataAction;

async function getActiveCartProducts(cartState: ICartState, cart: Cart, ctx: IActionContext): Promise<SimpleProductExt[]> {
    // If there are cart lines, make call to get products
    if (!cartState.hasInvoiceLine && cart && cart.CartLines && cart.CartLines.length > 0) {
        ctx.trace('Getting cart product information...');

        try {
            const products = await getSimpleProducts(
                <ProductInput[]>cart.CartLines.map(cartLine => {
                    if (cartLine.ProductId) {
                        return new ProductInput(
                            cartLine.ProductId,
                            ctx.requestContext.apiSettings,
                            undefined,
                            undefined,
                            ctx.requestContext,
                            cartLine.CatalogId
                        );
                    }
                    return undefined;
                }).filter(Boolean),
                ctx
            );

            return products || [];
        } catch (error) {
            ctx.trace((error as Error).toString());
            ctx.telemetry.error((error as Error).message);
            ctx.telemetry.debug('[getActiveCartWithProducts]Unable to hydrate cart with product information');
            throw new Error('[getActiveCartWithProducts]Unable to hydrate cart with product information');
        }
    }

    ctx.trace('[getActiveCartWithProducts]No Products Found in cart');
    return [];
}

async function getActiveCartProductsActionWhenQuantityLimitsFeatureIsOn(cart: Cart, ctx: IActionContext): Promise<SimpleProductExt[]> {
    const productIdsByWarehouseId: Map<string, CartLine[]> = new Map();
    let resultProducts: SimpleProductExt[] = [];
    cart.CartLines?.forEach(cartLine =>
        productIdsByWarehouseId.has(cartLine.WarehouseId!)
            ? productIdsByWarehouseId.get(cartLine.WarehouseId!)?.push(cartLine)
            : productIdsByWarehouseId.set(cartLine.WarehouseId!, [cartLine])
    );
    return Promise.all(
        [...productIdsByWarehouseId].map(([entryWarehouseId, entryProductIds]) => {
            return getSimpleProducts(
                <ProductInput[]>entryProductIds
                    .map(cartLine => {
                        if (cartLine.ProductId) {
                            return new ProductInput(
                                cartLine.ProductId,
                                ctx.requestContext.apiSettings,
                                undefined,
                                entryWarehouseId,
                                ctx.requestContext,
                                cartLine.CatalogId
                            );
                        }
                        return undefined;
                    })
                    .filter(Boolean),
                ctx
            ).then((products: SimpleProductExt[]) => {
                if (products) {
                    resultProducts = products.reduce((accum, product) => {
                        if (product) {
                            accum.push(product);
                        }
                        return accum;
                    }, resultProducts);
                }
            });
        })
    ).then(() => resultProducts);
}

async function isOrderQuantityLimitsFeatureEnabled(ctx: IActionContext): Promise<boolean> {
    const defaultOrderQuantityLimitsFeatureConfig = ctx.requestContext.app?.platform?.enableDefaultOrderQuantityLimits;
    if (defaultOrderQuantityLimitsFeatureConfig === 'none') {
        return Promise.resolve(false);
    }

    const featureStates = await getFeatureState(createGetFeatureStateInput(ctx), ctx);
    let isQuantityLimitsFeatureEnabledInHq = false;
    if (ArrayExtensions.hasElements(featureStates)) {
        isQuantityLimitsFeatureEnabledInHq =
            featureStates.find(
                (featureState: FeatureState) => featureState.Name === 'Dynamics.AX.Application.RetailDefaultOrderQuantityLimitsFeature'
            )?.IsEnabled || false;
    }

    if (!isQuantityLimitsFeatureEnabledInHq) {
        return false;
    }

    if (defaultOrderQuantityLimitsFeatureConfig === 'all') {
        return Promise.resolve(true);
    }

    return getCustomer(new GetCustomerInput(ctx.requestContext.apiSettings), ctx)
        .then(customerInfo => {
            return (
                !!customerInfo &&
                ((defaultOrderQuantityLimitsFeatureConfig === 'b2b' && customerInfo.IsB2b) ||
                    (defaultOrderQuantityLimitsFeatureConfig === 'b2c' && !customerInfo.IsB2b))
            );
        })
        .catch((error: Error) => {
            ctx.telemetry.warning(error.message);
            ctx.telemetry.debug('Unable to get customer info');
            return false;
        });
}
